/* eslint-disable react-hooks/exhaustive-deps */
import _ from "lodash";
import { useAtom } from "jotai";
import toast from "react-hot-toast";
import axiosInstance from "helpers/api";
import SimpleLeadAI from "components/chat/SimpleLeadAI";
import InviteCustomerModal from "components/modals/InviteCustomerModal";

import ScrollSpy from "components/base/ScrollSpy";
import Backdrop from "components/common/Backdrop";
import { user_info } from "jotai/UserJotaiInstance";
import { Fragment, useEffect, useState } from "react";
import NoDataFound from "components/common/NoDataFound";
import CustomLoader from "components/common/CustomLoader";
import LeadProfileCard from "components/cards/LeadProfileCard";
// Universal System aligned workflow panel
import UniversalWorkflowPanel from "components/cards/UniversalWorkflowPanel";
import EnhancedLeadContext from "components/cards/EnhancedLeadContext";
import LeadPrePromptWidget from "components/cards/LeadPrePromptWidget";
// Agent Kairos workflow task approvals
import AgentKairosWorkflowPanel from "components/modules/crm/ai/AgentKairosWorkflowPanel";
// Workflow alerts now integrated into LeadAI chatbot instead of separate card
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import LeadDetailsOffcanvas from "components/modules/crm/LeadDetailsOffcanvas";
import LeadDetailsTab from "components/modules/crm/lead-details/LeadDetailsTab";
import ConfirmDeleteModal from "components/modules/crm/common-components/ConfirmDeleteModal";
import InlineEmailComposer from "components/modules/crm/InlineEmailComposer";
import {
  paginationAtom,
  leadDetailsMeetingsAtom,
} from "jotai/LeadsJotaiInstance";
import { Helmet } from "react-helmet";
import './LeadDetails.css'
import { debugAuthIssue } from '../../../utils/debugAuth';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faEdit, 
  faTrash, 
  faEnvelope, 
  faPhone, 
  faUser, 
  faBuilding,
  faMapMarkerAlt,
  faCalendar,
  faClock,
  faChartBar,
  faDollarSign,
  faRocket,
  faRobot,
  faBolt,
  faChartLine
} from "@fortawesome/free-solid-svg-icons";
import { Badge, Button, Row, Col } from "react-bootstrap";
import AIStatusIndicator from "components/common/AIStatusIndicator";
import { getInitials } from "helpers/utils";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { leadDetailsBreadcrumbItems } from "data/commonData";


interface UserInfo {
  payment_status?: string;
  [key: string]: any;
}


interface LeadDetailsType {
  id: string;
  lead_first_name?: string;
  lead_last_name?: string;
  lead_email?: string;
  lead_phone?: string;
  lead_meetings: any[];
  [key: string]: any;
}

const LeadDetails = () => {
  console.log('🔍 [LEAD DETAILS] Rendering LeadDetails component');
  // NOTE: LeadDetails.tsx is a container component that renders other components.
  // For lead_ai_context updates, see LeadAiAssistant.tsx which handles AI analysis context refresh.
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [status, setStatus] = useState("idle");
  const [callNotes, setCallNotes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [meetingNotes, setMeetingNotes] = useState([]);

  const [openOffcanvas, setOpenOffcanvas] = useState(false);
  const [showLeadDetails, setShowLeadDetails] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showInlineEmailComposer, setShowInlineEmailComposer] = useState(false);
  const [fetchedLeadDetails, setFetchedLeadDetails] = useState(false);
  const [leadDetails, setLeadDetails] = useState<LeadDetailsType>({
    id: "",
    lead_meetings: [],
  });

  const [workflowRefreshTrigger, setWorkflowRefreshTrigger] = useState(0);
  const [workflowAlerts, setWorkflowAlerts] = useState<any[]>([]);
  const [workflowData, setWorkflowData] = useState<any>(null);
  const [leadScore, setLeadScore] = useState<number>(0);
  // Workflow alerts now handled by LeadAI chatbot instead of separate UI component

  /**
   * Handle workflow data loaded from UniversalWorkflowPanel
   * Generate proactive alerts from the existing data
   */
  const handleWorkflowDataLoaded = (data: any) => {
    console.log('🔄 [WORKFLOW DATA] Received workflow data from UniversalWorkflowPanel:', data);
    
    setWorkflowData(data);
    
    if (data) {
      // Generate alerts from the loaded workflow data
      const alerts = generateWorkflowAlertsFromData(data);
      console.log(`🔔 [WORKFLOW ALERTS] Generated ${alerts.length} alerts from workflow data`);
      
      setWorkflowAlerts(alerts);
      
      // Show notification for critical/high priority alerts
      const criticalAlerts = alerts.filter(
        (alert: any) => alert.priority === 'critical' || alert.priority === 'high'
      );
      
      if (criticalAlerts.length > 0) {
        const alertMessage = criticalAlerts.length === 1 
          ? `⚠️ Urgent: ${criticalAlerts[0].message}`
          : `⚠️ You have ${criticalAlerts.length} urgent workflow tasks due`;
        
        toast.error(alertMessage, {
          duration: 8000, // Show longer for important alerts
          icon: '🔔'
        });
      }
    } else {
      // Clear alerts if no data
      setWorkflowAlerts([]);
    }
  };

  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get("type");

  const [userInfo] = useAtom<UserInfo>(user_info);
  const [pagination, setPagination] = useAtom(paginationAtom);
  const { limit, offset, queryType } = pagination;
  const [, setLeadDetailsMeeting] = useAtom(leadDetailsMeetingsAtom);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthorized = userInfo?.payment_status === "Cancelled" ? false : true;



  useEffect(() => {
    if (!isAuthorized) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "childList") {
            const backdropExists = document.getElementById("kairos-backdrop");
            if (backdropExists) {
              setShowLeadDetails(false);
            } else {
              setShowLeadDetails(true);
            }
          }
        });
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
      return () => observer.disconnect();
    }
  }, [isAuthorized]);

  const handleDeleteLead = async (id: any) => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.delete(`/leads/${id}`);
      toast.success(response.data.message);
      navigate("/leads");
    } catch (error: any) {
      toast.error(error.response.data.message);
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
    }
  };

  const fetchCallNotes = async () => {
    try {
      const response = await axiosInstance.get(`/leads/${id}/activities`, {
        params: { type: "call_note" },
      });
      setCallNotes(response.data.data.notes);
    } catch (error: any) {
      console.log(error.response.data.message);
    }
  };

  const fetchMeetingNotes = async () => {
    try {
      const response = await axiosInstance.get(`/leads/${id}/activities`, {
        params: { type: "meeting" },
      });
      setMeetingNotes(response.data.data.meetings);
    } catch (error: any) {
      console.log(error.response.data.message);
    }
  };

  const fetchLeadScore = async () => {
    try {
      // Fetch the lead evaluation which contains the behavioral analysis score
      const response = await axiosInstance.get(`/leads/${id}/evaluation`);
      if (response.data.success && response.data.evaluation) {
        const score = response.data.evaluation.score || 0;
        setLeadScore(score);
        console.log('📊 [HEADER SCORE] Updated lead score:', score);
      }
    } catch (error: any) {
      console.log('[Lead Score Error]', error.response?.data?.message || error.message);
      
      // If evaluation not found, trigger universal evaluation
      if (error.response?.status === 404 && error.response?.data?.error?.includes('Run universal evaluation first')) {
        console.log('🚀 [LEAD SCORE] Triggering universal evaluation for lead:', id);
        try {
          const evalResponse = await axiosInstance.post(`/leads/${id}/trigger-evaluation`, {
            evaluationType: 'workflow_trigger'
          });
          
          if (evalResponse.data.success && evalResponse.data.evaluation_data?.evaluation) {
            console.log('✅ [LEAD SCORE] Universal evaluation triggered successfully');
            
            // Use the evaluation data directly from the trigger response
            const evaluation = evalResponse.data.evaluation_data.evaluation;
            const score = evaluation.confidence_score || evaluation.score || 0;
            setLeadScore(score);
            console.log('📊 [HEADER SCORE] Updated lead score from trigger response:', score);
            
            // Also try fetching from the evaluation endpoint after a delay
            // in case the data gets persisted eventually
            setTimeout(async () => {
              try {
                const retryResponse = await axiosInstance.get(`/leads/${id}/evaluation`);
                if (retryResponse.data.success && retryResponse.data.evaluation) {
                  const score = retryResponse.data.evaluation.score || 0;
                  setLeadScore(score);
                  console.log('📊 [HEADER SCORE] Updated lead score from GET endpoint:', score);
                }
              } catch (retryError) {
                console.log('[Lead Score Retry Error - using trigger response data]', retryError);
              }
            }, 2000); // Wait 2 seconds for evaluation to process
          }
        } catch (evalError) {
          console.log('[Trigger Evaluation Error]', evalError);
        }
      }
      // Don't show error toast - fallback to 0
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await axiosInstance.get(`/leads/${id}/activities`, {
        params: { type: "note" },
      });
      setNotes(response.data.data.notes);
    } catch (error: any) {
      console.log(error.response.data.message);
    }
  };

  const fetchTaskData = async () => {
    try {
      const response = await axiosInstance.get(`/leads/${id}/activities`, {
        params: { type: "task" },
      });
      setTasks(response.data.data.tasks);
    } catch (error: any) {
      console.log(error.response.data.message);
    }
  };

  const fetchAttachmentData = async () => {
    try {
      const response = await axiosInstance.get(`/leads/${id}/activities`, {
        params: { type: "attachment" },
      });
      setAttachments(response.data.data.attachments);
    } catch (error: any) {
      console.log(error.response.data.message);
    }
  };

  const fetchSearchActivityResults = async (searchQuery: string) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/leads/${id}/activities`, {
        params: { q: searchQuery },
      });
      setActivities(response.data.data.activities);
    } catch (err) {
      toast.error("Error fetching search results");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSearchMeetingResults = async (searchQuery: string) => {
    try {
      const response = await axiosInstance.get(`/leads/${id}/activities`, {
        params: { q: searchQuery, type: "meeting" },
      });
      setMeetingNotes(response.data.data.meetings);
    } catch (err) {
      toast.error("Error fetching search results");
    }
  };

  const fetchSearchTaskResults = async (searchQuery: string) => {
    try {
      const response = await axiosInstance.get(`/leads/${id}/activities`, {
        params: { q: searchQuery, type: "task" },
      });
      setTasks(response.data.data.tasks);
    } catch (err) {
      toast.error("Error fetching search results");
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchLeadData = async () => {
      try {
        setStatus("loading");
        const response = await axiosInstance.get(`/leads/${id}`, {
          signal: controller.signal,
        });
        setLeadDetails(response.data.data.lead);
        setLeadDetailsMeeting(response.data.data.lead.lead_meetings);
        setFetchedLeadDetails(true);
        
        // NEW: Workflow alerts will be generated when UniversalWorkflowPanel loads its data
        // This is more efficient than making a separate API call that was failing
        console.log('✅ [LEAD DETAILS] Lead data loaded successfully, ID:', id);
        console.log('📋 [LEAD DETAILS] Workflow alerts will be generated from UniversalWorkflowPanel data');
        
        // CRITICAL: Refresh lead_ai_context to include workflow data for chatbot
        // This ensures the chatbot has access to current tasks and workflow information
        if (id) {
          setTimeout(() => {
            console.log('🔄 [CONTEXT REFRESH] Triggering lead_ai_context refresh to include workflow data');
            refreshLeadAiContext(id);
          }, 2000); // Delay to allow WorkflowProgressPanel to load first
        }
        
      } catch (error: any) {
        const msg = error?.response?.data?.message || "Failed to fetch lead data.";
        toast.error(msg);
        setFetchedLeadDetails(false);
      } finally {
        setStatus("idle");
      }
    };

    if (id) {
      fetchLeadData();
      fetchLeadScore(); // Fetch the actual lead score for the header
    }

    return () => {
      controller.abort();
    };
  }, [id]);

  useEffect(() => {
    const controller = new AbortController();
    if (type === "" || type === "activity" || type === "emails") {
      const fetchActivityData = async () => {
        try {
          setIsLoading(true);
          // For emails, pass type=email to the activities endpoint
          const activityType = type === "emails" ? "email" : queryType;
          const response = await axiosInstance.get(`/leads/${id}/activities`, {
            params: { limit, offset, type: activityType },
          });
          setActivities(response.data.data.activities);
          setPagination((prev) => ({
            ...prev,
            total: response.data.data.pagination.total,
          }));
        } catch (error: any) {
          console.log('[Activities Error]', error.response?.data?.message || error.message);
          // Don't show error toast - activities might be empty which is OK
        } finally {
          setIsLoading(false);
        }
      };
      fetchActivityData();
    }

    return () => {
      controller.abort();
    };
  }, [type, limit, offset]);

  useEffect(() => {
    if (type === "call") {
      fetchCallNotes();
    }
  }, [type]);

  useEffect(() => {
    if (type === "meeting") {
      fetchMeetingNotes();
    }
  }, [type]);

  useEffect(() => {
    if (type === "notes") {
      fetchNotes();
    }
  }, [type]);

  useEffect(() => {
    if (type === "task") {
      fetchTaskData();
    }
  }, [type]);

  useEffect(() => {
    if (type === "attachments") {
      fetchAttachmentData();
    }
  }, [type]);

  const handleSearchInputChange = _.debounce((searchQuery: any) => {
    fetchSearchActivityResults(searchQuery.target.value);
  }, 500);

  const handleSearchMeeting = _.debounce((searchQuery: any) => {
    fetchSearchMeetingResults(searchQuery.target.value);
  }, 500);

  const handleSearchTask = _.debounce((searchQuery: any) => {    
    fetchSearchTaskResults(searchQuery);
  }, 500);

  const refreshWorkflowProgress = () => {
    setWorkflowRefreshTrigger(prev => prev + 1);
  };

  const refreshActivities = async () => {
    try {
      setIsLoading(true);
      // For emails, pass type=email to the activities endpoint
      const activityType = type === "emails" ? "email" : queryType;
      const response = await axiosInstance.get(`/leads/${id}/activities`, {
        params: { limit, offset, type: activityType },
      });
      setActivities(response.data.data.activities);
      setPagination((prev) => ({
        ...prev,
        total: response.data.data.pagination.total,
      }));
    } catch (error: any) {
      console.error('Failed to refresh activities:', error);
      toast.error("Failed to refresh activities timeline");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Process existing workflow data to generate proactive alerts
   * This replaces the failing API call with local processing of already-loaded data
   */
  const generateWorkflowAlertsFromData = (workflowData: any) => {
    if (!workflowData) return [];
    
    const alerts: any[] = [];
    const now = new Date();
    
    console.log('🔍 [ALERT GENERATION] Processing workflow data:', {
      leadTasks: workflowData.leadTasks?.length || 0,
      upcomingTasks: workflowData.upcomingTasks?.length || 0,
      aiRecommendations: workflowData.aiRecommendations?.length || 0
    });
    
    // Process different types of tasks from UniversalWorkflowPanel data
    const taskSources = [
      { tasks: workflowData.leadTasks || [], type: 'lead_task' },
      { tasks: workflowData.upcomingTasks || [], type: 'upcoming_task' },
      { tasks: workflowData.aiRecommendations || [], type: 'ai_recommendation' }
    ];
    
    taskSources.forEach(({ tasks, type }) => {
      tasks.forEach((task: any) => {
        // Skip completed tasks
        if (task.status === 'COMPLETED' || task.isCompleted) return;
        
        // Get due date from different possible fields
        let dueDate = null;
        if (task.dueDate) {
          dueDate = new Date(task.dueDate);
        } else if (task.scheduledTime) {
          dueDate = new Date(task.scheduledTime);
        } else if (task.createdOn) {
          // Fallback: assume tasks should be completed within 24 hours if no due date
          dueDate = new Date(new Date(task.createdOn).getTime() + (24 * 60 * 60 * 1000));
        } else {
          // Skip tasks without any time reference
          return;
        }
        
        const timeDiff = dueDate.getTime() - now.getTime();
        const hoursUntilDue = timeDiff / (1000 * 60 * 60);
        
        // Create alerts for tasks that are due within 48 hours or overdue (but not more than 7 days overdue)
        if (hoursUntilDue <= 48 && hoursUntilDue > -168) { // Within 48 hours or overdue by less than 7 days
          const alert = {
            workflow_id: task.id || `${type}_${Date.now()}_${Math.random()}`,
            workflow_type: task.taskType || task.action || task.title || task.category || 'Task',
            workflow_stage: 'current',
            due_date: dueDate.toISOString(),
            hours_until_due: Math.round(hoursUntilDue * 10) / 10,
            priority: getDuePriorityFromHours(hoursUntilDue),
            status: task.status || 'PENDING',
            alert_type: hoursUntilDue > 0 ? 'due_soon' : 'overdue',
            message: generateAlertMessageFromTask(task, hoursUntilDue, type),
            actions: [
              { action: 'view_workflow', label: 'View Details', icon: 'eye' },
              { action: 'complete_task', label: 'Complete', icon: 'check' },
              { action: 'snooze_1_hour', label: 'Snooze 1h', icon: 'clock' },
              { action: 'dismiss', label: 'Dismiss', icon: 'times' }
            ],
            metadata: {
              created_at: task.createdOn || task.createdAt || new Date().toISOString(),
              workflow_progress: workflowData?.currentWorkflow?.overallProgress || 0,
              template_name: task.title || task.description || 'Task',
              task_type: type,
              confidence: task.confidence,
              priority_original: task.priority
            }
          };
          
          console.log(`🔔 [ALERT GENERATION] Created alert for ${type}:`, {
            id: alert.workflow_id,
            type: alert.workflow_type,
            hours_until_due: alert.hours_until_due,
            priority: alert.priority,
            message: alert.message
          });
          
          alerts.push(alert);
        }
      });
    });
    
    // Sort by priority and urgency
    alerts.sort((a, b) => {
      const priorityOrder: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
      const aPriority = priorityOrder[a.priority] || 0;
      const bPriority = priorityOrder[b.priority] || 0;
      const priorityDiff = bPriority - aPriority;
      if (priorityDiff !== 0) return priorityDiff;
      return a.hours_until_due - b.hours_until_due;
    });
    
    console.log(`✅ [ALERT GENERATION] Generated ${alerts.length} total alerts`);
    return alerts;
  };
  
  // Helper functions for alert generation
  const getDuePriorityFromHours = (hoursUntilDue: number) => {
    if (hoursUntilDue <= 0) return 'critical'; // Overdue
    if (hoursUntilDue <= 2) return 'high';     // Due within 2 hours  
    if (hoursUntilDue <= 8) return 'medium';   // Due within 8 hours
    return 'low'; // Due within 24 hours
  };
  
  const generateAlertMessageFromTask = (task: any, hoursUntilDue: number, taskType: string = 'task') => {
    const taskName = task.title || task.description || task.action || 'Task';
    const typePrefix = taskType === 'ai_recommendation' ? 'AI Recommendation' : 'Task';
    
    if (hoursUntilDue <= 0) {
      const hoursOverdue = Math.abs(hoursUntilDue);
      if (hoursOverdue < 1) {
        return `🚨 ${typePrefix}: "${taskName}" is overdue by ${Math.round(hoursOverdue * 60)} minutes`;
      } else if (hoursOverdue < 24) {
        return `🚨 ${typePrefix}: "${taskName}" is overdue by ${Math.round(hoursOverdue)} hours`;
      } else {
        const daysOverdue = Math.round(hoursOverdue / 24);
        return `🚨 ${typePrefix}: "${taskName}" is overdue by ${daysOverdue} days`;
      }
    } else if (hoursUntilDue < 1) {
      const minutesUntilDue = Math.round(hoursUntilDue * 60);
      return `⏰ ${typePrefix}: "${taskName}" due in ${minutesUntilDue} minutes`;
    } else if (hoursUntilDue <= 24) {
      return `📅 ${typePrefix}: "${taskName}" due in ${Math.round(hoursUntilDue)} hours`;
    } else if (hoursUntilDue <= 48) {
      return `📋 ${typePrefix}: "${taskName}" due tomorrow`;
    }
    
    return `📋 ${typePrefix}: "${taskName}" requires attention`;
  };

  /**
   * DEPRECATED: Old approach that was failing due to API issues
   * Replaced with local processing of existing workflow data
   */
  const refreshLeadAiContext = async (leadId: string) => {
    try {
      console.log(`🔄 [AI CONTEXT REFRESH] Starting refresh for lead: ${leadId}`);
      console.log(`🔄 [AI CONTEXT REFRESH] Making POST request to: /leads/${leadId}/context/refresh`);
      
      const response = await axiosInstance.post(`/leads/${leadId}/context/refresh`);
      
      if (response.data.success) {
        console.log(`✅ [AI CONTEXT REFRESH] Successfully refreshed context:`, response.data.data);
        console.log(`📧 [AI CONTEXT REFRESH] Processed ${response.data.data.emails_processed} emails`);
        console.log(`📧 [AI CONTEXT REFRESH] Found ${response.data.data.new_emails_found} new emails`);
        console.log(`🎯 [AI CONTEXT REFRESH] Context completeness: ${response.data.data.context_completeness}%`);
        console.log(`💾 [AI CONTEXT REFRESH] Cache refreshed: ${response.data.data.cache_refreshed}`);
        
        // 🚀 PROACTIVE WORKFLOW ALERTS - Handle workflow alerts for LeadAI chatbot
        if (response.data.data.workflow_alerts && response.data.data.workflow_alerts.length > 0) {
          console.log(`🔔 [WORKFLOW ALERTS] Found ${response.data.data.alert_count} workflow alerts`);
          setWorkflowAlerts(response.data.data.workflow_alerts);
          
          // Show notification for critical/high priority alerts
          const criticalAlerts = response.data.data.workflow_alerts.filter(
            (alert: any) => alert.priority === 'critical' || alert.priority === 'high'
          );
          
          if (criticalAlerts.length > 0) {
            const alertMessage = criticalAlerts.length === 1 
              ? `⚠️ Urgent: ${criticalAlerts[0].message}`
              : `⚠️ You have ${criticalAlerts.length} urgent workflow tasks due`;
            
            toast.error(alertMessage, {
              duration: 8000, // Show longer for important alerts
              icon: '🔔'
            });
          }
        } else {
          // Clear any existing alerts if none found
          setWorkflowAlerts([]);
        }
        
        // 🎯 NEW: Handle workflow re-evaluation results
        if (response.data.data.workflow_re_evaluation) {
          const workflowEval = response.data.data.workflow_re_evaluation;
          console.log(`🔄 [WORKFLOW RE-EVAL] Evaluation results:`, workflowEval);
          
          if (workflowEval.workflow_changed) {
            console.log(`🎯 [WORKFLOW CHANGE] Workflow changed to: ${workflowEval.recommended_workflow}`);
            console.log(`📝 [WORKFLOW CHANGE] Reasoning: ${workflowEval.reasoning}`);
            
            // Trigger UniversalWorkflowPanel refresh to show new AI recommendations
            console.log(`🔄 [WORKFLOW REFRESH] Triggering UniversalWorkflowPanel refresh for new AI recommendations`);
            setWorkflowRefreshTrigger(prev => prev + 1);
            
            // Show notification about workflow change
            toast.success(`🎯 Workflow updated: ${workflowEval.recommended_workflow.replace(/_/g, ' ')}`, {
              duration: 6000,
              icon: '🚀'
            });
          }
        }
        
        // Show success notification only if new emails were processed
        if (response.data.data.new_emails_found > 0) {
          toast.success(`Updated lead context with ${response.data.data.new_emails_found} new emails`);
        }
      } else {
        console.warn(`⚠️ [AI CONTEXT REFRESH] Refresh completed with warnings:`, response.data);
      }
    } catch (error: any) {
      console.error(`❌ [AI CONTEXT REFRESH] Failed to refresh context for lead ${leadId}:`, error);
      console.error(`❌ [AI CONTEXT REFRESH] Error status: ${error.response?.status}`);
      console.error(`❌ [AI CONTEXT REFRESH] Error message: ${error.response?.data?.error || error.message}`);
      console.error(`❌ [AI CONTEXT REFRESH] Full error response:`, error.response?.data);
      
      // Only show error toast for meaningful errors (not 404 or expected failures)
      if (error.response?.status !== 404) {
        const errorMessage = error.response?.data?.error || 'Failed to update lead context';
        console.warn(`⚠️ [AI CONTEXT REFRESH] Error message: ${errorMessage}`);
      }
      
      // Don't show error toast to user as this is a background operation
      // The chatbot will still work with existing cached data
    }
  };

  /**
   * NEW: Trigger real-time workflow progression
   * SAFE IMPLEMENTATION: Uses new backend endpoint with error handling
   */
  const triggerWorkflowProgression = async (activityData: any) => {
    try {
      console.log('🚀 [WORKFLOW PROGRESSION] Triggering workflow advancement for lead:', id);
      console.log('📋 [WORKFLOW PROGRESSION] Activity data:', activityData);
      
      if (!id) {
        console.warn('⚠️ [WORKFLOW PROGRESSION] No lead ID available');
        return;
      }
      
      const response = await axiosInstance.post(`/workflow/progress/${id}`, {
        activity_type: activityData.type || 'unknown',
        completion_data: activityData.data || {},
        trigger_source: activityData.source || 'manual'
      });
      
      console.log('✅ [WORKFLOW PROGRESSION] Response received:', response.data);
      
      if (response.data.success && response.data.progressed) {
        console.log('🎯 [WORKFLOW PROGRESSION] Workflow successfully advanced to:', response.data.new_stage);
        
        // Refresh workflow data to reflect new stage
        setWorkflowRefreshTrigger(prev => prev + 1);
        
        // Show success notification
        toast.success(`🎯 Workflow advanced to: ${response.data.new_stage.stage_name}`, {
          duration: 6000,
          icon: '🚀'
        });
        
        // Refresh AI context with new workflow state (delayed to allow UI updates)
        setTimeout(() => {
          console.log('🔄 [WORKFLOW PROGRESSION] Refreshing AI context after progression');
          if (id) {
            refreshLeadAiContext(id);
          }
        }, 2000);
        
      } else if (response.data.success && !response.data.progressed) {
        console.log('📋 [WORKFLOW PROGRESSION] Progression not triggered:', response.data.reason);
        
        // Show informational message (only if not "no workflow found")
        if (!response.data.reason?.includes('No active workflow')) {
          toast(`📋 Workflow stage criteria not yet met: ${response.data.suggestion || 'Complete more activities'}`, {
            duration: 4000,
            icon: '📋'
          });
        }
      } else {
        console.warn('⚠️ [WORKFLOW PROGRESSION] Unexpected response format:', response.data);
      }
      
    } catch (error: any) {
      console.error('❌ [WORKFLOW PROGRESSION] Failed to advance workflow:', error);
      
      // Only show error for meaningful errors (not expected workflow states)
      if (error.response?.status !== 404 && error.response?.status !== 200) {
        toast.error('Failed to advance workflow stage', {
          duration: 3000
        });
      }
    }
  };

  /**
   * Handle workflow alert actions from within LeadAI chatbot
   */
  const handleWorkflowAlertAction = async (alert: any, action: string) => {
    console.log(`🔔 [WORKFLOW ALERT ACTION] ${action} for workflow:`, alert.workflow_id);
    
    try {
      switch (action) {
        case 'view_workflow':
          // Navigate to workflow details or open modal
          console.log(`👁️ Viewing workflow details for: ${alert.workflow_id}`);
          toast.success(`Opening workflow details...`);
          // TODO: Implement workflow details view
          break;
          
        case 'complete_task':
          // Mark workflow task as complete
          console.log(`✅ Marking workflow complete: ${alert.workflow_id}`);
          toast.success(`Workflow task marked as complete!`);
          // Remove alert from list
          setWorkflowAlerts(prev => prev.filter(a => a.workflow_id !== alert.workflow_id));
          
          // NEW: Trigger workflow progression after task completion
          setTimeout(() => {
            triggerWorkflowProgression({
              type: 'task_completed',
              data: {
                workflow_id: alert.workflow_id,
                task_type: alert.workflow_type,
                completion_time: new Date().toISOString()
              },
              source: 'alert_action'
            });
          }, 1000); // Small delay to allow UI updates
          break;
          
        case 'snooze_1_hour':
          // Snooze alert for 1 hour
          console.log(`⏰ Snoozing alert for 1 hour: ${alert.workflow_id}`);
          toast.success(`Alert snoozed for 1 hour`);
          // Remove from current alerts (will reappear in 1 hour)
          setWorkflowAlerts(prev => prev.filter(a => a.workflow_id !== alert.workflow_id));
          break;
          
        case 'reschedule':
          // Open reschedule modal/form
          console.log(`📅 Rescheduling workflow: ${alert.workflow_id}`);
          toast.success(`Opening reschedule options...`);
          // TODO: Implement reschedule functionality
          break;
          
        case 'dismiss':
          // Dismiss alert
          console.log(`❌ Dismissing alert: ${alert.workflow_id}`);
          setWorkflowAlerts(prev => prev.filter(a => a.workflow_id !== alert.workflow_id));
          break;
          
        default:
          console.warn(`Unknown workflow alert action: ${action}`);
      }
    } catch (error) {
      console.error(`❌ Error handling workflow alert action:`, error);
      toast.error(`Failed to ${action.replace('_', ' ')}`);
    }
  };

  if (status === "loading") {
    return <CustomLoader />;
  }

  if (status === "idle" && Object.keys(leadDetails).length === 0) {
    return <NoDataFound type="Leads" customClass="mt-xl-15" />;
  }

  if (status === "idle" && fetchedLeadDetails) {
    return (
      <Fragment>
        <Helmet>
          <meta charSet="utf-8" />
          <title>KairosIQ.ai - {leadDetails?.lead_first_name}</title>
        </Helmet>
        {(isAuthorized || showLeadDetails) ? (
          <Fragment>
            <div className="ai-lead-details-container">
              <div className="mb-9">
                <Row className="align-items-center justify-content-between g-3 mb-4">
                  <Col md="auto">
                    <PageBreadcrumb items={leadDetailsBreadcrumbItems} />
                  </Col>
                </Row>
                
                {/* AI Intelligence Header - Enhanced Lead Details */}
                <div 
                  className="intelligence-header mb-4"
                  style={{
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                    borderRadius: '16px',
                    padding: '24px',
                    border: '1px solid rgba(0, 212, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    {/* Top Section */}
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex align-items-center">
                        <div style={{ position: 'relative', marginRight: '20px' }}>
                          <div
                            style={{
                              width: '60px',
                              height: '60px',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '24px',
                              color: 'white',
                              fontWeight: '600',
                              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                            }}
                          >
                            {getInitials(leadDetails?.lead_first_name, leadDetails?.lead_last_name)}
                          </div>
                        </div>
                        <div>
                          <h2 style={{ 
                            color: '#1f2937', 
                            fontWeight: 600, 
                            marginBottom: '8px',
                            textShadow: '0 0 20px rgba(0, 212, 255, 0.3)',
                            fontSize: '1.8rem'
                          }}>
                            {leadDetails?.lead_first_name} {leadDetails?.lead_last_name || ''}
                          </h2>
                          <div className="d-flex align-items-center gap-3">
                            {leadDetails?.lead_status?.status && (
                              <Badge 
                                bg="primary"
                                style={{
                                  background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                                  border: 'none',
                                  borderRadius: '8px',
                                  fontSize: '12px',
                                  padding: '6px 12px',
                                  fontWeight: '600'
                                }}
                              >
                                {leadDetails.lead_status.status}
                              </Badge>
                            )}
                            {leadDetails?.lead_type?.lead_type && (
                              <Badge
                                bg="success"
                                style={{
                                  background: 'rgba(16, 185, 129, 0.1)',
                                  color: '#059669',
                                  borderRadius: '8px',
                                  fontSize: '12px',
                                  padding: '6px 12px',
                                  fontWeight: '600',
                                  border: '1px solid rgba(16, 185, 129, 0.2)'
                                }}
                              >
                                {leadDetails.lead_type.lead_type}
                              </Badge>
                            )}
                            {/* Lead Score if available */}
                            <div 
                              className="lead-score-display d-flex align-items-center"
                              style={{
                                background: 'rgba(249, 115, 22, 0.1)',
                                borderRadius: '12px',
                                padding: '6px 12px',
                                border: '1px solid rgba(249, 115, 22, 0.2)'
                              }}
                            >
                              <FontAwesomeIcon icon={faChartLine} className="text-warning me-2" />
                              <span className="fw-bold" style={{ fontSize: '14px', color: '#ea580c' }}>
                                Score: {leadScore}/100
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    
                      {/* AI Features - Top Right */}
                      <div className="d-flex align-items-center gap-2">
                        {/* AI Agent Activation/Status */}
                        <Button
                          variant="primary"
                          className="ai-activation-header-btn"
                          onClick={() => toast.success('🚀 AI Agent activation coming soon!')}
                          style={{
                            background: 'linear-gradient(135deg, #00d4ff 0%, #10b981 100%)',
                            border: 'none',
                            borderRadius: '24px',
                            padding: '10px 20px',
                            fontWeight: '600',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 4px 16px rgba(0, 212, 255, 0.3)',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 212, 255, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 212, 255, 0.3)';
                          }}
                        >
                          <FontAwesomeIcon icon={faBolt} />
                          Activate AI Agent
                        </Button>

                        {/* Send Email Button */}
                        <Button
                          variant="primary"
                          className="ai-activation-header-btn ms-2"
                          onClick={() => {
                            if (leadDetails?.lead_email) {
                              setShowInlineEmailComposer(!showInlineEmailComposer);
                            } else {
                              toast.error('No email address available');
                            }
                          }}
                          disabled={!leadDetails?.lead_email}
                          style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            borderRadius: '24px',
                            padding: '10px 20px',
                            fontSize: '13px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                            transition: 'all 0.3s ease',
                            cursor: leadDetails?.lead_email ? 'pointer' : 'not-allowed',
                            opacity: leadDetails?.lead_email ? 1 : 0.6
                          }}
                          onMouseEnter={(e) => {
                            if (leadDetails?.lead_email) {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.4)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (leadDetails?.lead_email) {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.3)';
                            }
                          }}
                        >
                          <FontAwesomeIcon icon={faEnvelope} />
                          {showInlineEmailComposer ? 'Close Email' : 'Send Email'}
                        </Button>
                        
                        {/* Dynamic AI Status Indicator */}
                        {/* AIStatusIndicator temporarily disabled due to prop mismatch */}
                      </div>
                    </div>

                    {/* Middle Section - Contact Info */}
                    <div 
                      className="d-flex gap-4 mb-3 p-3"
                      style={{
                        background: 'rgba(255, 255, 255, 0.5)',
                        borderRadius: '12px',
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      {leadDetails?.lead_email && (
                        <div className="contact-item d-flex align-items-center gap-2">
                          <FontAwesomeIcon icon={faEnvelope} style={{ color: '#00d4ff', fontSize: '16px' }} />
                          <strong style={{ color: '#1f2937', fontSize: '14px' }}>Email:</strong>
                          <span style={{ fontSize: '14px', color: '#4b5563' }}>{leadDetails.lead_email}</span>
                        </div>
                      )}
                      {leadDetails?.lead_phone && (
                        <div className="contact-item d-flex align-items-center gap-2">
                          <FontAwesomeIcon icon={faPhone} style={{ color: '#00d4ff', fontSize: '16px' }} />
                          <strong style={{ color: '#1f2937', fontSize: '14px' }}>Phone:</strong>
                          <span style={{ fontSize: '14px', color: '#4b5563' }}>{leadDetails.lead_phone}</span>
                        </div>
                      )}
                      {leadDetails?.lead_company && (
                        <div className="contact-item d-flex align-items-center gap-2">
                          <FontAwesomeIcon icon={faBuilding} style={{ color: '#00d4ff', fontSize: '16px' }} />
                          <strong style={{ color: '#1f2937', fontSize: '14px' }}>Company:</strong>
                          <span style={{ fontSize: '14px', color: '#4b5563' }}>{leadDetails.lead_company}</span>
                        </div>
                      )}
                    </div>

                    {/* Bottom Section */}
                    <div className="d-flex align-items-end justify-content-between">
                      {/* Additional Lead Info */}
                      <div className="d-flex align-items-center gap-4">
                        {leadDetails?.created_on && (
                          <span style={{ fontSize: '14px', color: '#6b7280' }}>
                            <FontAwesomeIcon icon={faCalendar} className="me-1" style={{ color: '#00d4ff' }} />
                            Created: {new Date(leadDetails.created_on).toLocaleDateString()}
                          </span>
                        )}
                        {leadDetails?.updated_on && (
                          <span style={{ fontSize: '14px', color: '#6b7280' }}>
                            <FontAwesomeIcon icon={faClock} className="me-1" style={{ color: '#00d4ff' }} />
                            Last Activity: {new Date(leadDetails.updated_on).toLocaleDateString()}
                          </span>
                        )}
                        <span style={{ fontSize: '14px', color: '#6b7280' }}>
                          <FontAwesomeIcon icon={faChartBar} className="me-1" style={{ color: '#00d4ff' }} />
                          Engagement: High
                        </span>
                      </div>

                      {/* Edit/Delete Actions - Bottom Right */}
                      <div className="d-flex align-items-center gap-2">
                        <Button
                          as={Link}
                          to={`/edit-lead/${leadDetails?.id}`}
                          variant="outline-primary"
                          size="sm"
                          style={{
                            border: '1px solid rgba(102, 126, 234, 0.3)',
                            borderRadius: '12px',
                            padding: '6px 14px',
                            fontSize: '12px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s ease',
                            background: 'rgba(255, 255, 255, 0.8)',
                            textDecoration: 'none'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.border = '1px solid transparent';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
                            e.currentTarget.style.color = 'inherit';
                            e.currentTarget.style.border = '1px solid rgba(102, 126, 234, 0.3)';
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                          <span>Edit</span>
                        </Button>
                        {/* Invite Customer Button */}
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => setShowInviteModal(true)}
                          disabled={!leadDetails?.lead_email}
                          title={!leadDetails?.lead_email ? "Lead must have an email address" : "Invite to customer portal"}
                          style={{
                            border: '1px solid rgba(102, 126, 234, 0.3)',
                            borderRadius: '12px',
                            padding: '6px 14px',
                            fontSize: '12px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s ease',
                            background: 'rgba(255, 255, 255, 0.8)',
                            marginLeft: '8px',
                            marginRight: '8px',
                            opacity: !leadDetails?.lead_email ? 0.6 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (leadDetails?.lead_email) {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                              e.currentTarget.style.color = 'white';
                              e.currentTarget.style.border = '1px solid transparent';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
                            e.currentTarget.style.color = 'inherit';
                            e.currentTarget.style.border = '1px solid rgba(102, 126, 234, 0.3)';
                          }}
                        >
                          <FontAwesomeIcon icon={faEnvelope} />
                          <span>Invite</span>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => setShowDeleteModal(true)}
                          style={{
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '12px',
                            padding: '6px 14px',
                            fontSize: '12px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s ease',
                            background: 'rgba(255, 255, 255, 0.8)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.border = '1px solid transparent';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
                            e.currentTarget.style.color = 'inherit';
                            e.currentTarget.style.border = '1px solid rgba(239, 68, 68, 0.3)';
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                          <span>Delete</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Neural Network Background Pattern */}
                  <div 
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `
                        radial-gradient(circle at 20% 20%, rgba(0, 212, 255, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 80% 80%, rgba(102, 126, 234, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 50% 50%, rgba(118, 75, 162, 0.05) 0%, transparent 70%)
                      `,
                      pointerEvents: 'none'
                    }}
                  />
                </div>
                
                {/* Inline Email Composer */}
                <InlineEmailComposer
                  lead={{
                    id: leadDetails.id,
                    lead_email: leadDetails.lead_email || '',
                    lead_first_name: leadDetails.lead_first_name,
                    lead_last_name: leadDetails.lead_last_name
                  }}
                  isOpen={showInlineEmailComposer}
                  onClose={() => setShowInlineEmailComposer(false)}
                  onEmailSent={() => {
                    // Refresh activities after email is sent
                    refreshActivities();
                  }}
                />
                
                {/* Main Lead Content - Modern Layout */}
                <div className="modern-lead-details-layout d-flex gap-4">
                {/* Sidebar - hidden on mobile */}
                <div className="d-none d-md-block" style={{ width: "360px", flexShrink: 0 }}>
                  <div className="sticky-leads-sidebar">
                    {(() => {
                      const currentLeadId = leadDetails?.id || id;
                      return currentLeadId ? (
                        <>
                          {/* AI Pre-Prompt Recommendations */}
                          <LeadPrePromptWidget 
                            leadId={currentLeadId}
                            onActionComplete={() => {
                              // Refresh workflow after action
                              refreshWorkflowProgress();
                              // Also refresh activities to show new emails/calls
                              refreshActivities();
                            }}
                          />
                          
                          <UniversalWorkflowPanel 
                            className="mb-3" 
                            leadId={currentLeadId}
                            refreshTrigger={workflowRefreshTrigger}
                            onProactiveAlerts={setWorkflowAlerts}
                          />
                          
                          {/* Agent Kairos Workflow Task Approvals */}
                          <AgentKairosWorkflowPanel
                            className="mb-3"
                            leadId={currentLeadId}
                            refreshTrigger={workflowRefreshTrigger}
                          />
                          
                          <EnhancedLeadContext
                            className="mb-3"
                            leadId={currentLeadId}
                            onTasksGenerated={(tasks) => {
                              console.log('🎯 [HAIKU TASKS] Generated tasks:', tasks);
                              // Trigger workflow refresh to show new tasks
                              setWorkflowRefreshTrigger(prev => prev + 1);
                            }}
                          />
                        </>
                      ) : null;
                    })()}
                    
                    {/* NEW: Test Workflow Progression Button (Development/Testing) */}
                    <div className="mb-3">
                      <button 
                        className="btn btn-outline-primary btn-sm w-100"
                        onClick={() => triggerWorkflowProgression({
                          type: 'manual_test',
                          data: { test: true, trigger_time: new Date().toISOString() },
                          source: 'manual_test'
                        })}
                        title="Test workflow progression (Development)"
                      >
                        🚀 Test Workflow Progression
                      </button>
                    </div>
                  </div>
                </div>

                {/* Mobile Sidebar - Only workflow panels on mobile */}
                <div className="d-block d-md-none w-100 mb-3">
                  <div className="mobile-lead-sidebar px-2">
                    {(() => {
                      const currentLeadId = leadDetails?.id || id;
                      return currentLeadId ? (
                        <>
                          {/* AI Pre-Prompt Recommendations - Mobile */}
                          <LeadPrePromptWidget 
                            leadId={currentLeadId}
                            onActionComplete={() => {
                              refreshWorkflowProgress();
                              refreshActivities();
                            }}
                          />
                          
                          <UniversalWorkflowPanel 
                            className="mb-3" 
                            leadId={currentLeadId}
                            refreshTrigger={workflowRefreshTrigger}
                            onProactiveAlerts={setWorkflowAlerts}
                          />
                          
                          {/* Agent Kairos Workflow Task Approvals - Mobile */}
                          <AgentKairosWorkflowPanel
                            className="mb-3"
                            leadId={currentLeadId}
                            refreshTrigger={workflowRefreshTrigger}
                          />
                          
                          <EnhancedLeadContext
                            className="mb-3"
                            leadId={currentLeadId}
                            onTasksGenerated={(tasks) => {
                              console.log('🎯 [HAIKU TASKS] Generated tasks:', tasks);
                              // Trigger workflow refresh to show new tasks
                              setWorkflowRefreshTrigger(prev => prev + 1);
                            }}
                          />
                        </>
                      ) : null;
                    })()}
                  </div>
                </div>
                
                {/* Main Content */}
                <div className="flex-grow-1 d-flex flex-column mobile-content-container">
                  <div className="lead-details-container">
                    <ScrollSpy>
                      <ScrollSpy.Content
                        id="lead-details"
                        minTopValue={150}
                        className="lead-details-scrollspy mb-8"
                      >
                        <LeadDetailsTab
                          tasks={tasks}
                          notes={notes}
                          isLoading={isLoading}
                          callNotes={callNotes}
                          activities={activities}
                          leadDetails={leadDetails}
                          meetingNotes={meetingNotes}
                          attachmentsLv={attachments}
                          handleSearchTask={handleSearchTask}
                          handleSearchMeeting={handleSearchMeeting}
                          handleSearchInputChange={handleSearchInputChange}
                          onWorkflowProgressRefresh={refreshWorkflowProgress}
                          onRefreshActivities={refreshActivities}
                          onComposeEmail={() => setShowInlineEmailComposer(true)}
                        />
                      </ScrollSpy.Content>
                    </ScrollSpy>
                  </div>
                </div>
              </div>
              </div>{/* Close modern-lead-details-layout */}
              </div>{/* Close mb-9 */}

              <LeadDetailsOffcanvas
                open={openOffcanvas}
                leadDetails={leadDetails}
                setOpen={setOpenOffcanvas}
              />

              <ConfirmDeleteModal
                type="Lead"
                modalShow={showDeleteModal}
                setModalShow={setShowDeleteModal}
                handleDelete={() => handleDeleteLead(leadDetails?.id)}
              />

              {/* Invite Customer Modal */}
              <InviteCustomerModal
                show={showInviteModal}
                onHide={() => setShowInviteModal(false)}
                leadId={leadDetails?.id}
                dealId={leadDetails?.deal_id}
                dealName={`${leadDetails?.lead_first_name || ''} ${leadDetails?.lead_last_name || ''} - Property Search`}
                leadDetails={{
                  id: leadDetails?.id,
                  lead_first_name: leadDetails?.lead_first_name,
                  lead_last_name: leadDetails?.lead_last_name,
                  lead_email: leadDetails?.lead_email
                }}
                dealDetails={{
                  customers: [{
                    lead_id: leadDetails?.id,
                    lead_first_name: leadDetails?.lead_first_name,
                    lead_last_name: leadDetails?.lead_last_name,
                    lead_email: leadDetails?.lead_email
                  }]
                }}
                mode="lead"
              />

              {/* Simple Lead AI Assistant - Only render when lead data is ready */}
              {leadDetails?.id && userInfo?.id && (
                <SimpleLeadAI 
                  leadId={leadDetails.id}
                  leadFirstName={leadDetails.lead_first_name || 'Lead'}
                  leadLastName={leadDetails.lead_last_name || ''}
                  userId={userInfo.id}
                  workflowAlerts={workflowAlerts}
                  onWorkflowAction={handleWorkflowAlertAction}
                />
              )}
          </Fragment>
        ) : (
          <Backdrop />
        )}
      </Fragment>
    );
  }

  return null;
};

export default LeadDetails;
